import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import { sessionManager } from "@/auth/sessionBridge";
import { buildLocaleHeader } from "@/i18n/localeHeader";

function getAuthHeader(): Record<string, string> {
  const session = sessionManager.getSession();
  if (session?.token) {
    return { Authorization: `Bearer ${session.token}` };
  }
  return {};
}

/** `VITE_API_ENDPOINT` without a trailing slash, the base of every platform admin schema. */
function getApiBaseUrl(): string {
  const endpoint: string = import.meta.env.VITE_API_ENDPOINT ?? "";
  return endpoint.endsWith("/") ? endpoint.slice(0, -1) : endpoint;
}

export interface IPlatformAdminClientOptions {
  /** Extra request headers resolved per request (e.g. the active marketing workspace id). */
  headers?: () => Record<string, string>;
  /** Links placed before the auth link (e.g. the stale-workspace error link). */
  links?: ApolloLink[];
}

/**
 * Apollo client for one platform admin GraphQL schema (`/platform/admin/{module}/graphql`)
 * under `VITE_API_ENDPOINT`. Every schema shares the same links: bearer token, locale header,
 * HotChocolate's CSRF preflight header and multipart uploads.
 */
export function createPlatformAdminClient(
  schemaPath: string,
  { headers, links = [] }: IPlatformAdminClientOptions = {},
): ApolloClient {
  // Upload link sends File variables as multipart/form-data (GraphQL multipart request
  // spec) instead of JSON, so `Upload!` mutations work.
  const httpLink = new UploadHttpLink({ uri: `${getApiBaseUrl()}${schemaPath}` });

  const authLink = new SetContextLink(({ headers: requestHeaders }) => ({
    headers: {
      ...(requestHeaders || {}),
      ...buildLocaleHeader(),
      ...getAuthHeader(),
      ...(headers?.() ?? {}),
      // Required by HotChocolate's CSRF protection for multipart (file upload) requests.
      "GraphQL-preflight": "1",
    },
  }));

  return new ApolloClient({
    link: ApolloLink.from([...links, authLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
      },
    },
  });
}
